/**
 * Unified Wallet Interface for MineOrDie
 * Supports: MetaMask, Coinbase Wallet, Smart Wallet (with gas sponsorship)
 *
 * New simplified contract model:
 * - Players fund their account once with fund()
 * - Server handles match start/settle via authorized signer
 * - Players can withdraw available balance anytime
 */

import type { Address, Hex } from 'viem';
import { USE_SMART_WALLET, ENTRY_FEE } from './config.js';

// Coinbase Wallet (regular EOA)
import {
  connectWallet as connectCoinbaseWallet,
  disconnectWallet as disconnectCoinbaseWallet,
  isWalletConnected as isCoinbaseConnected,
  getWalletAddress as getCoinbaseAddress,
  getUSDCBalance as getCoinbaseUSDCBalance,
  getGameBalance as getCoinbaseGameBalance,
  getAvailableGameBalance as getCoinbaseAvailableBalance,
  fundGameAccount as fundCoinbase,
  withdrawFromGame as withdrawCoinbase,
  canPlay as canPlayCoinbase,
  formatUSDC,
  // Admin functions
  getAccumulatedDevFees,
  getContractUSDCBalance,
  getDevFeeBps,
  getPlayerBalance,
} from './wallet-manager.js';

// Smart Wallet (with gas sponsorship)
import {
  connectSmartWallet,
  disconnectSmartWallet,
  isSmartWalletConnected,
  getSmartWalletAddress,
  getSmartWalletUSDCBalance,
  isGasSponsorshipAvailable,
} from './smart-wallet.js';

// Injected Wallet (MetaMask, Rabby, etc.)
import {
  connectInjectedWallet,
  disconnectInjectedWallet,
  isInjectedWalletConnected,
  getInjectedWalletAddress,
  getInjectedWalletUSDCBalance,
  isInjectedWalletAvailable,
  getInjectedWalletName,
  isInjectedSponsorshipAvailable,
  setInjectedSponsorship,
  isInjectedSponsorshipEnabled,
  getInjectedGameBalance,
  getInjectedAvailableBalance,
  fundInjectedGameAccount,
  withdrawInjectedFromGame,
  canPlayInjected,
} from './injected-wallet.js';

/**
 * Wallet types supported
 */
export type WalletType = 'smart' | 'coinbase' | 'injected' | null;

// Track which wallet type is in use
let activeWalletType: WalletType = null;

/**
 * Get available wallet options
 */
export function getAvailableWallets(): { type: WalletType; name: string; recommended?: boolean }[] {
  const wallets: { type: WalletType; name: string; recommended?: boolean }[] = [];

  // Smart Wallet is always available (Coinbase creates it)
  wallets.push({
    type: 'smart',
    name: 'Smart Wallet (Gas Free)',
    recommended: true,
  });

  // Coinbase Wallet
  wallets.push({
    type: 'coinbase',
    name: 'Coinbase Wallet',
  });

  // Injected wallet (MetaMask, etc.)
  if (isInjectedWalletAvailable()) {
    wallets.push({
      type: 'injected',
      name: getInjectedWalletName(),
    });
  }

  return wallets;
}

/**
 * Get active wallet type
 */
export function getActiveWalletType(): WalletType {
  return activeWalletType;
}

/**
 * Check if any wallet is connected
 */
export function isConnected(): boolean {
  switch (activeWalletType) {
    case 'smart':
      return isSmartWalletConnected();
    case 'coinbase':
      return isCoinbaseConnected();
    case 'injected':
      return isInjectedWalletConnected();
    default:
      return false;
  }
}

/**
 * Get connected wallet address
 */
export function getAddress(): Address | null {
  switch (activeWalletType) {
    case 'smart':
      return getSmartWalletAddress();
    case 'coinbase':
      return getCoinbaseAddress();
    case 'injected':
      return getInjectedWalletAddress();
    default:
      return null;
  }
}

/**
 * Connect a specific wallet type
 */
export async function connect(type: WalletType = 'smart'): Promise<Address> {
  // Disconnect any existing wallet first
  if (activeWalletType) {
    disconnect();
  }

  let address: Address;

  switch (type) {
    case 'smart':
      address = await connectSmartWallet();
      activeWalletType = 'smart';
      break;

    case 'coinbase':
      address = await connectCoinbaseWallet();
      activeWalletType = 'coinbase';
      break;

    case 'injected':
      if (!isInjectedWalletAvailable()) {
        throw new Error('No browser wallet found. Please install MetaMask.');
      }
      address = await connectInjectedWallet();
      activeWalletType = 'injected';
      break;

    default:
      throw new Error(`Unknown wallet type: ${type}`);
  }

  console.log(`Connected ${type} wallet:`, address);
  return address;
}

/**
 * Connect with automatic wallet selection
 * Tries Smart Wallet first, falls back to injected, then Coinbase
 */
export async function connectAuto(): Promise<Address> {
  // If user prefers Smart Wallet and it's available
  if (USE_SMART_WALLET) {
    try {
      return await connect('smart');
    } catch (e) {
      console.warn('Smart wallet failed:', e);
    }
  }

  // Try injected wallet (MetaMask) if available
  if (isInjectedWalletAvailable()) {
    try {
      return await connect('injected');
    } catch (e) {
      console.warn('Injected wallet failed:', e);
    }
  }

  // Fall back to Coinbase Wallet
  return await connect('coinbase');
}

/**
 * Disconnect wallet
 */
export function disconnect(): void {
  switch (activeWalletType) {
    case 'smart':
      disconnectSmartWallet();
      break;
    case 'coinbase':
      disconnectCoinbaseWallet();
      break;
    case 'injected':
      disconnectInjectedWallet();
      break;
  }
  activeWalletType = null;
}

/**
 * Get USDC balance in wallet (not funded to game yet)
 */
export async function getUSDCBalance(): Promise<bigint> {
  switch (activeWalletType) {
    case 'smart':
      return await getSmartWalletUSDCBalance();
    case 'coinbase':
      return await getCoinbaseUSDCBalance();
    case 'injected':
      return await getInjectedWalletUSDCBalance();
    default:
      return 0n;
  }
}

/**
 * Get game balance (funded in MineOrDie contract)
 */
export async function getGameBalance(): Promise<bigint> {
  switch (activeWalletType) {
    case 'smart':
      // TODO: implement for smart wallet
      return 0n;
    case 'coinbase':
      return await getCoinbaseGameBalance();
    case 'injected':
      return await getInjectedGameBalance();
    default:
      return 0n;
  }
}

/**
 * Get available game balance (can be used to play or withdraw)
 */
export async function getAvailableBalance(): Promise<bigint> {
  switch (activeWalletType) {
    case 'smart':
      // TODO: implement for smart wallet
      return 0n;
    case 'coinbase':
      return await getCoinbaseAvailableBalance();
    case 'injected':
      return await getInjectedAvailableBalance();
    default:
      return 0n;
  }
}

/**
 * Fund game account with USDC
 * This is the main action players take to deposit money
 */
export async function fund(amount: bigint): Promise<Hex> {
  switch (activeWalletType) {
    case 'smart':
      // TODO: implement sponsored version for smart wallet
      throw new Error('Smart wallet fund not yet implemented');
    case 'coinbase':
      return await fundCoinbase(amount);
    case 'injected':
      return await fundInjectedGameAccount(amount);
    default:
      throw new Error('No wallet connected');
  }
}

/**
 * Withdraw USDC from game account
 */
export async function withdraw(amount: bigint): Promise<Hex> {
  switch (activeWalletType) {
    case 'smart':
      // TODO: implement sponsored version for smart wallet
      throw new Error('Smart wallet withdraw not yet implemented');
    case 'coinbase':
      return await withdrawCoinbase(amount);
    case 'injected':
      return await withdrawInjectedFromGame(amount);
    default:
      throw new Error('No wallet connected');
  }
}

/**
 * Check if player can play (has enough funded balance)
 */
export async function canPlay(wagerAmount?: bigint): Promise<boolean> {
  switch (activeWalletType) {
    case 'smart':
      // TODO: implement for smart wallet
      return false;
    case 'coinbase':
      return await canPlayCoinbase(wagerAmount);
    case 'injected':
      return await canPlayInjected(wagerAmount);
    default:
      return false;
  }
}

/**
 * Check if gas sponsorship is active for the current wallet
 */
export function hasGasSponsorship(): boolean {
  if (activeWalletType === 'smart') {
    return isGasSponsorshipAvailable();
  }
  if (activeWalletType === 'injected') {
    return isInjectedSponsorshipEnabled();
  }
  return false;
}

/**
 * Check if gas sponsorship can be enabled for the current wallet
 */
export function canEnableGasSponsorship(): boolean {
  if (activeWalletType === 'smart') {
    return isGasSponsorshipAvailable();
  }
  if (activeWalletType === 'injected') {
    return isInjectedSponsorshipAvailable();
  }
  return false;
}

/**
 * Enable or disable gas sponsorship for injected wallets
 * Smart wallets always use sponsorship when available
 */
export function setGasSponsorship(enabled: boolean): void {
  if (activeWalletType === 'injected') {
    setInjectedSponsorship(enabled);
  }
  // Smart wallets always use sponsorship, coinbase doesn't support it
}

/**
 * Get wallet display name
 */
export function getWalletDisplayName(): string {
  switch (activeWalletType) {
    case 'smart':
      return isGasSponsorshipAvailable() ? 'Smart Wallet (Gas Free)' : 'Smart Wallet';
    case 'coinbase':
      return 'Coinbase Wallet';
    case 'injected':
      const name = getInjectedWalletName();
      return isInjectedSponsorshipEnabled() ? `${name} (Gas Free)` : name;
    default:
      return 'Not Connected';
  }
}

/**
 * Check if MetaMask is available
 */
export function hasMetaMask(): boolean {
  return isInjectedWalletAvailable() && getInjectedWalletName() === 'MetaMask';
}

// Legacy exports for backwards compatibility (deprecated)
export async function stake(_escrowAddress: Address): Promise<Hex> {
  // Legacy: just check if player can play
  const canPlayerPlay = await canPlay();
  if (!canPlayerPlay) {
    // Fund with minimum entry fee
    return await fund(ENTRY_FEE);
  }
  return '0x' as Hex;
}

export async function claim(_escrowAddress: Address): Promise<Hex> {
  // Legacy: withdraw all available balance
  const available = await getAvailableBalance();
  if (available > 0n) {
    return await withdraw(available);
  }
  return '0x' as Hex;
}

export async function hasStaked(_escrowAddress: Address): Promise<boolean> {
  return await canPlay();
}

export async function getClaimableReward(_escrowAddress: Address): Promise<bigint> {
  return await getAvailableBalance();
}

// Re-export utilities
export { formatUSDC };

// Admin wallet address (lowercase for comparison)
const ADMIN_WALLET = '0xcd81a01160780d28773ba0771c708ed3312b9c67' as Address;

/**
 * Check if currently connected wallet is the admin wallet
 * Uses the unified getAddress() so it works with any wallet type
 */
export function isAdmin(): boolean {
  const address = getAddress();
  if (!address) return false;
  return address.toLowerCase() === ADMIN_WALLET.toLowerCase();
}

// Re-export admin functions from wallet-manager (except isAdmin which we define above)
export {
  getAccumulatedDevFees,
  getContractUSDCBalance,
  getDevFeeBps,
  getPlayerBalance,
};
